import * as React from 'react'

import * as formik from 'formik'
import styled from 'styled-components'
import * as yup from 'yup'

import * as api from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'

type CommentFormProps = {
  autoFocus?: boolean
  currentUser: api.AuthenticatedUser
  onCancel?: () => void
  onSubmit: (values: CommentFormValue) => Promise<void>
  submitLabel?: string
}

type CommentFormValue = {
  comment: string
}

const schema = yup.object().shape({
  comment: yup.string().max(500).required(),
})

export function CommentForm({
  autoFocus,
  currentUser,
  onCancel,
  onSubmit,
  submitLabel = t('Reply'),
}: CommentFormProps): React.ReactElement {
  const $textAreaRef = React.useRef<() => void>(null)

  return (
    <formik.Formik<CommentFormValue>
      initialValues={{
        comment: '',
      }}
      onSubmit={submit}
      validate={validate}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {(formikBag) => {
        const [isPrivacyToggleOpen, setPrivacyToggleOpen] = React.useState(false)

        return (
          <Wrapper>
            <plume.styles.image.Avatar src={models.user.avatar(currentUser, '90x90')} type="extra-small" />
            <Form onSubmit={formikBag.handleSubmit}>
              <formik.Field name="comment">
                {({ field, meta }: formik.FieldProps<CommentFormValue['comment'], CommentFormValue>) => {
                  return (
                    <plume.TextAreaField
                      /* eslint-disable-next-line jsx-a11y/no-autofocus */
                      autoFocus={autoFocus}
                      error={meta.error}
                      maxLength={500}
                      name={field.name}
                      onChange={field.onChange}
                      placeholder={t('Your comment')}
                      resetRef={$textAreaRef}
                      value={field.value}
                    />
                  )
                }}
              </formik.Field>
              <SubmitCTA type="submit">{submitLabel}</SubmitCTA>
              {onCancel && !formikBag.isSubmitting && (
                <plume.LinkAsButton kind="secondary" type="button" onClick={onCancel}>
                  {t('Cancel')}
                </plume.LinkAsButton>
              )}
              <plume.styles.copy.S>
                {t('Comments are public, we invite you to be careful.')}{' '}
                <PrivacyToggle kind="primary" type="button" onClick={() => setPrivacyToggleOpen(!isPrivacyToggleOpen)}>
                  {t('Learn why')}.
                </PrivacyToggle>
              </plume.styles.copy.S>
              {isPrivacyToggleOpen && (
                <>
                  <plume.styles.copy.S>{t('IMPORTANT: Through your comment, you could:')}</plume.styles.copy.S>
                  <plume.styles.copy.S>
                    {t(
                      'reveal information about you, which can sometimes be sensitive or very personal (religious or philosophical beliefs, sexual orientation, political opinions, state of health, etc.)',
                    )}
                  </plume.styles.copy.S>
                  <plume.styles.copy.S>
                    {t(
                      'Once your comment is published, this information will be public and available to all. We invite you to be careful in writing your comments, to protect your privacy',
                    )}
                  </plume.styles.copy.S>
                </>
              )}
            </Form>
          </Wrapper>
        )
      }}
    </formik.Formik>
  )

  async function submit(values: CommentFormValue, helpers: formik.FormikHelpers<CommentFormValue>): Promise<void> {
    await onSubmit(values)
    $textAreaRef.current && $textAreaRef.current()
    helpers.resetForm()
  }

  function validate(values: CommentFormValue): Promise<formik.FormikErrors<CommentFormValue>> {
    const validation = schema.validate(values)
    return validation.then(
      () => ({}),
      (err) => {
        // TODO: Toast or flash message?
        return formik.yupToFormErrors(err)
      },
    )
  }
}

const Form = styled.form`
  ${plume.LinkAsButton} {
    margin: -10px 0 4px;
  }

  ${plume.styles.copy.S}:not(:first-child) {
    color: ${plume.COLORS.TEXT_TERTIARY};
    margin-top: 14px;
  }

  ${plume.TextAreaField} {
    ${plume.styles.field.Status} {
      align-items: baseline;
      justify-content: flex-end;
    }

    ${plume.styles.input.Count} {
      margin-left: 10px; // NOTE: This is to make room for the field error and allow it to be aligned to the right.
    }
  }
`

const PrivacyToggle = styled(plume.LinkAsButton)`
  padding: 0;
`

const SubmitCTA = styled(plume.Button)`
  margin: -6px auto 0;
`

const Wrapper = styled.section`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  margin-bottom: 14px;

  ${Form} {
    flex: 1;
    margin-left: 8px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-bottom: 24px;
  }
`
